import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Heart, MessageCircle, Flame, FolderPlus, Loader2, X, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

export default function VibeGallery() {
  const navigate = useNavigate();
  const [vibes, setVibes] = useState([]);
  const [trending, setTrending] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [collName, setCollName] = useState("");
  const [collEmoji, setCollEmoji] = useState("🎁");
  const [selected, setSelected] = useState([]);

  const loadColls = () => api.get("/fx/collections").then(({ data }) => setCollections(data.collections || [])).catch(() => {});

  useEffect(() => {
    api.get("/fx/gallery")
      .then(({ data }) => { setVibes(data.vibes || []); setTrending(data.trending || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadColls();
  }, []);

  const toggleLike = async (e, code) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/fx/shared-vibe/${code}/like`);
      const patch = (v) => (v.code === code ? { ...v, liked: data.liked, likes: data.likes } : v);
      setVibes((vs) => vs.map(patch));
      setTrending((ts) => ts.map(patch));
    } catch { /* ignore */ }
  };

  const toggleSelect = (code) => setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));

  const startCreate = () => { setShowCreate(true); setSelected([]); setCollName(""); setCollEmoji("🎁"); };
  const cancelCreate = () => { setShowCreate(false); setSelected([]); setCollName(""); };

  const createCollection = async () => {
    const name = collName.trim();
    if (!name || selected.length === 0 || creating) return;
    setCreating(true);
    try {
      await api.post("/fx/collections", { name, emoji: collEmoji || "🎁", codes: selected });
      toast.success(`Collection "${name}" created! 🎁`);
      setShowCreate(false); setCollName(""); setCollEmoji("🎁"); setSelected([]);
      loadColls();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create that."); }
    finally { setCreating(false); }
  };

  const renderCard = (v, trend = false) => {
    const isSel = selected.includes(v.code);
    const onOpen = () => (showCreate ? toggleSelect(v.code) : navigate(`/app/vibe/${v.code}`));
    return (
      <div key={trend ? `t-${v.code}` : v.code} role="button" tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
        data-testid={`gallery-vibe-${v.code}`}
        className={`group cursor-pointer text-left rounded-2xl border bg-[hsl(var(--card))] overflow-hidden hover:-translate-y-0.5 transition-all ${isSel && showCreate ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/50" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
        <div className="h-24 bg-gradient-to-br from-[#1f6feb] via-[#7c3aed] to-[#ec4899] grid place-items-center text-5xl relative">
          {v.emoji || "🎨"}
          {showCreate ? (
            <span data-testid={`gallery-select-${v.code}`}
              className={`absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center transition-colors ${isSel ? "bg-[hsl(var(--primary))] text-white" : "bg-black/40 text-white/60 border border-white/40"}`}>
              {isSel && <Check className="w-4 h-4" />}
            </span>
          ) : (
            <>
              {trend && v.recent_likes > 0 && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-orange-500/90 px-2 py-0.5 text-[10px] font-bold text-white">
                  <Flame className="w-3 h-3" /> +{v.recent_likes}
                </span>
              )}
              <button onClick={(e) => toggleLike(e, v.code)} data-testid={`gallery-like-${v.code}`}
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/40 backdrop-blur px-2 py-1 text-xs font-semibold text-white hover:bg-black/60 transition-colors">
                <Heart className={`w-3.5 h-3.5 ${v.liked ? "fill-rose-500 text-rose-500" : ""}`} /> {v.likes || 0}
              </button>
            </>
          )}
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-bold text-sm truncate">{v.name}</h3>
            <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-2">
            by {v.author_name}
            {v.comments_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {v.comments_count}</span>}
          </p>
          {Array.isArray(v.fx) && v.fx.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {v.fx.slice(0, 4).map((f, i) => <span key={i} className="text-[10px] rounded-full bg-[hsl(var(--secondary))] px-2 py-0.5 capitalize">{f}</span>)}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const rest = trending.length > 0 ? vibes.filter((v) => !trending.some((t) => t.code === v.code)) : vibes;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="vibe-gallery">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="font-display text-2xl font-extrabold">Vibe Gallery</h1>
        </div>
        {vibes.length > 0 && (
          showCreate ? (
            <button onClick={cancelCreate} data-testid="collection-create-cancel"
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold hover:bg-[hsl(var(--secondary))]">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          ) : (
            <button onClick={startCreate} data-testid="collection-create-start"
              className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-3.5 py-1.5 text-xs font-semibold hover:opacity-90">
              <FolderPlus className="w-3.5 h-3.5" /> New collection
            </button>
          )
        )}
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
        {showCreate
          ? "Pick the vibes you love, name the collection, then save it to share a whole set at once."
          : "Community atmospheres shared by creators. Tap any vibe to one-tap it onto one of your own games."}
      </p>

      {showCreate && (
        <div className="sticky top-2 z-10 mb-6 rounded-2xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--card))] p-3 flex flex-wrap items-center gap-2 shadow-lg" data-testid="collection-create-bar">
          <input value={collEmoji} onChange={(e) => setCollEmoji(e.target.value.slice(0, 4))} data-testid="collection-emoji-input"
            className="w-12 text-center text-xl rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] py-1.5" aria-label="Collection emoji" />
          <input value={collName} onChange={(e) => setCollName(e.target.value)} placeholder="Collection name…" data-testid="collection-name-input"
            className="flex-1 min-w-[160px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-sm" />
          <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="collection-selected-count">{selected.length} selected</span>
          <button onClick={createCollection} disabled={!collName.trim() || selected.length === 0 || creating} data-testid="collection-create-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-1.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create
          </button>
        </div>
      )}

      {!showCreate && collections.length > 0 && (
        <section className="mb-8" data-testid="vibe-collections">
          <h2 className="flex items-center gap-1.5 text-sm font-bold mb-3"><FolderPlus className="w-4 h-4 text-[hsl(var(--primary))]" /> Your collections</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.map((c) => (
              <button key={c.id} onClick={() => navigate(`/app/collection/${c.id}`)} data-testid={`collection-card-${c.id}`}
                className="flex items-center gap-3 text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 transition-all">
                <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] text-2xl shrink-0">{c.emoji || "🎁"}</div>
                <div className="min-w-0">
                  <h3 className="font-bold text-sm truncate">{c.name}</h3>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{(c.codes || []).length} vibes</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] ml-auto shrink-0" />
              </button>
            ))}
          </div>
        </section>
      )}

      {vibes.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Sparkles className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No shared vibes yet — be the first! Open any game&apos;s Quick FX tray, save a vibe, then tap Share.</p>
        </div>
      ) : (
        <>
          {!showCreate && trending.length > 0 && (
            <section className="mb-8" data-testid="vibe-trending">
              <h2 className="flex items-center gap-1.5 text-sm font-bold mb-3 text-orange-500"><Flame className="w-4 h-4" /> Trending this week</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {trending.map((v) => renderCard(v, true))}
              </div>
            </section>
          )}
          {(() => {
            const list = showCreate ? vibes : rest;
            if (list.length === 0) return null;
            return (
              <>
                <h2 className="text-sm font-bold mb-3 text-[hsl(var(--muted-foreground))]">{showCreate ? "Tap vibes to add" : (trending.length > 0 ? "More vibes" : "All vibes")}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((v) => renderCard(v, false))}
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
